import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { AutomationExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';
import { durationConverter } from 'utils/duration';

import { WaitModal } from '../../modals/wait';

export type Payload = {
  delayInSeconds: number;
  matchNodeId: string;
  nonMatchNodeId: string;
};

type WaitNodeAttributes = GenericNodeAttributes<Payload, AutomationExtraData>;
type WaitNodeProps = GenericNodeProps<Payload, AutomationExtraData>;

const translatePayload: WaitNodeAttributes['translatePayload'] = (node) => {
  const { nonMatchNodeId, matchNodeId } = node.payload;

  return [
    {
      id: uuid(),
      label: 'Trigger false',
      connection: nonMatchNodeId || uuid(),
    },
    {
      id: uuid(),
      label: 'Trigger still true',
      connection: matchNodeId || uuid(),
    },
  ];
};

const updateConnection: WaitNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const key =
    connection.connectionLabel === 'Trigger still true'
      ? 'matchNodeId'
      : 'nonMatchNodeId';
  const payload = { ...node.payload, [key]: connectionId };

  return { ...node, payload };
};

export const WaitNodeAttributes: WaitNodeAttributes = {
  iconName: 'calendar',
  label: 'Wait',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  NodeContent: WaitNode,
  ActionModal: WaitModal,
};

export function WaitNode({ node }: WaitNodeProps): JSX.Element {
  const { payload } = node;
  const { delayInSeconds } = payload;

  const waitTimeString = durationConverter.toString(delayInSeconds);

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text weight="semibold" text={waitTimeString} />
          </TextEllipsis>
        ),
        [waitTimeString],
      )}
    </TextContent>
  );
}
